<!-- Notifications (office-level scheduled reminder config) -->
<mat-card class="details-card notifications-card">
  <div class="notifications-section">
    <div class="card-heading">
      <div class="section-header">
        <div class="section-header__titles">
          <h3 class="notif-card-heading">
            <button
              type="button"
              class="notif-collapse-toggle"
              (click)="notificationsExpanded = !notificationsExpanded"
              [attr.aria-expanded]="notificationsExpanded"
            >
              <mat-icon
                class="expansion-indicator"
                [style.transform]="
                  notificationsExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                "
                >expand_more</mat-icon
              >
              Notifications
            </button>
          </h3>
        </div>
      </div>
      <p class="card-description">
        Send recurring notifications to people in this office.
      </p>
    </div>

    @if (notificationsExpanded) {
      <div class="notif-body" @expandCollapse>
        @if (isLoading) {
          <div class="notif-status">
            <mat-spinner diameter="28"></mat-spinner>
            <span>Loading notification settings…</span>
          </div>
        } @else if (loadError) {
          <div class="notif-status notif-status--error" role="alert">
            <mat-icon>error_outline</mat-icon>
            <span>{{ loadError }}</span>
            <button type="button" mat-stroked-button (click)="retry()">
              Retry
            </button>
          </div>
        } @else if (hasType) {
          <!-- Live summary of the current configuration -->
          <div class="notif-summary">
            <mat-icon class="notif-summary__icon">{{
              digestEnabled ? "notifications_active" : "notifications_off"
            }}</mat-icon>
            <div class="notif-summary__body">
              @if (!digestEnabled) {
                <p class="notif-summary__lead">
                  The resource capacity reminder is paused for
                  {{ officeDescription }}
                </p>
                <p class="notif-summary__detail">
                  Your saved schedule is kept, but nothing sends until you
                  re-enable it.
                </p>
              } @else {
                <p class="notif-summary__lead">
                  Resource capacity reminder is on for {{ officeDescription }}
                </p>
                <p class="notif-summary__detail">
                  Sends <strong>{{ notificationScheduleDaysPhrase }}</strong> at
                  <strong>{{ notificationScheduleTimesPhrase }}</strong> —
                  {{ notificationTimeZoneLabel }} —
                  <!-- A bare "to 0 practice areas" reads as a neutral count; at the top of the
                       card it needs to say that nothing actually goes out. -->
                  @if (enabledPracticeAreaCount === 0) {
                    but <strong>no practice areas are enabled</strong>, so it
                    reaches no one.
                  } @else {
                    to <strong>{{ enabledPracticeAreaCount }}</strong>
                    {{
                      enabledPracticeAreaCount === 1
                        ? "practice area"
                        : "practice areas"
                    }}
                    in this office who have not set their capacity for the
                    current period.
                  }
                </p>
              }
            </div>
            <mat-slide-toggle
              [(ngModel)]="digestEnabled"
              (change)="onNotificationChange()"
              [disabled]="!canManage"
              color="primary"
              class="toggle-success"
              [hideIcon]="true"
              aria-label="Enable the resource capacity reminder"
            ></mat-slide-toggle>
          </div>

          @if (digestEnabled) {
            <div class="notif-grid" @expandCollapse>
              <!-- Left column: schedule & timing (office-level) -->
              <div class="notif-col">
                <!-- Fixed eligibility rule (not configurable) — the sender only
                     ever targets these seniority levels, whatever is set here.
                     The list is a hand transcription of
                     ResourceCapacityReminderDefinition.RecipientSeniorityCodes
                     (JUNIOR / MID_LEVEL / SENIOR), which the API does not expose on
                     any DTO. Changing it server-side silently makes this copy wrong,
                     so update both together — here and in the recipients note below. -->
                <div class="notif-note notif-note--info">
                  <mat-icon class="notif-note__icon">info</mat-icon>
                  <span>
                    This reminder is only sent to resources whose seniority is
                    <strong>Junior</strong>, <strong>Mid-level</strong>, or
                    <strong>Senior</strong>. Resources at any other seniority
                    level never receive it, no matter how this notification is
                    configured.
                  </span>
                </div>

                <div class="notif-subsection">
                  <div class="notif-field">
                    <label class="notif-label">Frequency</label>
                    <div
                      class="freq-selector"
                      [class.freq-selector--daily]="
                        notificationFrequency === 'daily'
                      "
                    >
                      <span
                        class="freq-selector__thumb"
                        aria-hidden="true"
                      ></span>
                      <button
                        type="button"
                        class="mode-card"
                        [class.active]="notificationFrequency === 'specific'"
                        [attr.aria-pressed]="notificationFrequency === 'specific'"
                        [disabled]="!canManage"
                        (click)="setNotificationFrequency('specific')"
                      >
                        <mat-icon class="mode-card__icon">date_range</mat-icon>
                        <span class="mode-card__text">
                          <span class="mode-card__title">Specific days</span>
                          <span class="mode-card__sub"
                            >Choose days of the week</span
                          >
                        </span>
                      </button>
                      <button
                        type="button"
                        class="mode-card"
                        [class.active]="notificationFrequency === 'daily'"
                        [attr.aria-pressed]="notificationFrequency === 'daily'"
                        [disabled]="!canManage"
                        (click)="setNotificationFrequency('daily')"
                      >
                        <mat-icon class="mode-card__icon">event_repeat</mat-icon>
                        <span class="mode-card__text">
                          <span class="mode-card__title">Every day</span>
                          <span class="mode-card__sub">Sends every weekday</span>
                        </span>
                      </button>
                    </div>
                  </div>

                  <div class="notif-field">
                    <label class="notif-label">Days</label>
                    <div
                      class="day-picker"
                      [class.day-picker--locked]="daysLocked"
                      role="group"
                      aria-label="Days of the week"
                    >
                      @for (day of weekDays; track day) {
                        <button
                          type="button"
                          class="day-dot"
                          [class.selected]="selectedDays.includes(day)"
                          [attr.aria-pressed]="selectedDays.includes(day)"
                          [disabled]="daysLocked || !canManage"
                          (click)="toggleDay(day)"
                        >
                          {{ weekDayLabels[day] }}
                        </button>
                      }
                    </div>
                  </div>

                  <div class="notif-field">
                    <label class="notif-label">Time zone</label>
                    <mat-form-field
                      appearance="outline"
                      class="notif-select"
                      subscriptSizing="dynamic"
                    >
                      <mat-select
                        [(ngModel)]="notificationTimeZone"
                        (selectionChange)="onNotificationChange()"
                        [disabled]="!canManage"
                        aria-label="Time zone"
                      >
                        @for (tz of notificationTimeZones; track tz.value) {
                          <mat-option [value]="tz.value">{{
                            tz.label
                          }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                    <p class="notif-help">
                      Notifications send at 12:00 PM in the selected time zone.
                      Daylight-saving changes are handled automatically.
                    </p>
                  </div>

                  <div class="notif-lock">
                    <div class="notif-lock__text">
                      <span class="notif-lock__title"
                        >Required for all resources</span
                      >
                      <span class="notif-lock__desc">
                        When on, resources in this office can't turn this
                        notification off from their profile.
                      </span>
                    </div>
                    <mat-slide-toggle
                      [(ngModel)]="requiredForAllResources"
                      (change)="onNotificationChange()"
                      [disabled]="!canManage"
                      color="primary"
                      class="toggle-success"
                      [hideIcon]="true"
                      aria-label="Require this notification for all resources"
                    ></mat-slide-toggle>
                  </div>
                </div>
              </div>

              <!-- Right column: who receives it -->
              <div class="notif-col">
                <!-- Practice areas: per-(office, practice area) enable/disable -->
                <div class="notif-subsection">
                  <div class="section-header">
                    <div class="section-header__titles notif-subsection__titles">
                      <h4 class="notif-subtitle">Practice areas</h4>
                      <p class="notif-subdesc">
                        Choose which practice areas in this office receive the
                        notification.
                      </p>
                    </div>
                    <span class="pa-count">
                      <strong>{{ enabledPracticeAreaCount }}</strong> of
                      {{ notificationPracticeAreas.length }} enabled
                    </span>
                  </div>

                  <!-- Announcement channel for the warning below. It has to stay mounted with
                       only its text changing: a live region inserted at the same moment as its
                       content is not reliably announced, which is why the warning box itself
                       can't carry role="status". Visually hidden, and absolutely positioned by
                       the mixin so it adds no gap to this flex column. -->
                  <div class="pa-recipients-live" role="status">
                    @if (enabledPracticeAreaCount === 0) {
                      No practice areas are enabled, so this reminder currently
                      reaches no one.
                    }
                  </div>

                  <!-- The reminder is on and scheduled, but targets nobody until at least one
                       practice area is enabled — easy to leave in by accident, so call it out. -->
                  @if (enabledPracticeAreaCount === 0) {
                    <div class="notif-note notif-note--warn">
                      <mat-icon class="notif-note__icon">warning</mat-icon>
                      <span>
                        This reminder is turned on, but
                        <strong>no practice areas are enabled</strong>, so it
                        currently reaches no one. Enable at least one below for
                        it to start sending.
                      </span>
                    </div>
                  }

                  <div class="pa-toolbar">
                    <mat-form-field
                      appearance="outline"
                      class="pa-search"
                      subscriptSizing="dynamic"
                    >
                      <input
                        matInput
                        [ngModel]="practiceAreaFilter"
                        (ngModelChange)="onPracticeAreaFilterChange($event)"
                        placeholder="Filter practice areas"
                        aria-label="Filter practice areas"
                      />
                      <mat-icon
                        matSuffix
                        [style.cursor]="
                          practiceAreaFilter ? 'pointer' : 'default'
                        "
                        (click)="clearPracticeAreaFilter()"
                        >{{ practiceAreaFilter ? "close" : "search" }}</mat-icon
                      >
                    </mat-form-field>
                  </div>

                  <div class="pa-table-wrap">
                    <table
                      mat-table
                      [dataSource]="practiceAreaDataSource"
                      class="pa-table"
                    >
                      <!-- Name + code -->
                      <ng-container matColumnDef="practiceArea">
                        <th mat-header-cell *matHeaderCellDef>Practice area</th>
                        <td mat-cell *matCellDef="let pa">
                          <div class="pa-item__name" [matTooltip]="pa.name">
                            {{ pa.name }}
                          </div>
                          <div class="pa-item__meta">{{ pa.code }}</div>
                        </td>
                      </ng-container>

                      <!-- On/Off label -->
                      <ng-container matColumnDef="state">
                        <!-- The label and the toggle are one logical column to a reader,
                             so head them with a single centred "Status" spanning both.
                             This also labels the toggle column for assistive tech, which
                             would otherwise announce it as unlabeled. -->
                        <th
                          mat-header-cell
                          *matHeaderCellDef
                          class="pa-col-state"
                          [attr.colspan]="2"
                        >
                          Status
                        </th>
                        <td mat-cell *matCellDef="let pa" class="pa-col-state">
                          <span
                            class="pa-item__state"
                            [class.pa-item__state--on]="pa.enabled"
                            >{{ pa.enabled ? "On" : "Off" }}</span
                          >
                        </td>
                      </ng-container>

                      <!-- Per-practice-area toggle -->
                      <ng-container matColumnDef="enabled">
                        <!-- Empty and hidden: the "Status" header above spans this
                             column, so a second header cell would break the colspan. -->
                        <th
                          mat-header-cell
                          *matHeaderCellDef
                          class="pa-col-toggle"
                        ></th>
                        <td mat-cell *matCellDef="let pa" class="pa-col-toggle">
                          <mat-slide-toggle
                            [(ngModel)]="pa.enabled"
                            (change)="onPracticeAreaChange(pa)"
                            [disabled]="!canManage"
                            color="primary"
                            class="toggle-success"
                            [hideIcon]="true"
                            [attr.aria-label]="pa.name"
                          ></mat-slide-toggle>
                        </td>
                      </ng-container>

                      <tr
                        mat-header-row
                        *matHeaderRowDef="practiceAreaColumns"
                      ></tr>
                      <tr
                        mat-row
                        *matRowDef="let row; columns: practiceAreaColumns"
                      ></tr>

                      <tr class="mat-row" *matNoDataRow>
                        <td
                          class="mat-cell pa-no-data"
                          [attr.colspan]="practiceAreaColumns.length"
                        >
                          <!-- This row also shows when the office simply has no practice
                               areas at all, so only blame the filter when one is applied.
                               Branch on the data rather than `practiceAreaFilter`, which
                               stays truthy for a whitespace-only entry that filters nothing. -->
                          <div class="overrides-empty">
                            @if (notificationPracticeAreas.length === 0) {
                              No practice areas are available.
                            } @else {
                              No practice areas match your filter.
                            }
                          </div>
                        </td>
                      </tr>
                    </table>

                    <mat-paginator
                      class="pa-paginator"
                      [pageSize]="practiceAreaPageSize"
                      [pageSizeOptions]="practiceAreaPageSizeOptions"
                      showFirstLastButtons
                      aria-label="Select page of practice areas"
                    ></mat-paginator>
                  </div>

                  <!-- "Primary office" is load-bearing, not a flourish: the send query
                       (UserRepository.RecipientQuery) matches on ExternalEmployeeSync.OfficeCode
                       and deliberately ignores secondary offices, which exist only for in-app
                       visibility. The seniority list below is transcribed by hand from
                       ResourceCapacityReminderDefinition.RecipientSeniorityCodes; the API does
                       not expose it on any DTO, so the two must be kept in sync manually. -->
                  <div class="notif-note">
                    <mat-icon class="notif-note__icon">info</mat-icon>
                    <span>
                      Recipients are people whose
                      <strong>primary office</strong> is
                      <strong>{{ officeDescription }}</strong
                      >, at a <strong>Junior</strong>,
                      <strong>Mid-level</strong>, or <strong>Senior</strong>
                      seniority, <strong>and</strong> in an enabled practice area
                      above. Someone with this office as a secondary office is
                      not included. Someone in more than one enabled area is
                      notified once.
                      @if (requiredForAllResources) {
                        Everyone listed gets an email — this notification is
                        required for all resources in this office.
                      } @else {
                        Whether each person also gets an email follows their own
                        notification preference.
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          }
        } @else {
          <div class="notif-status">
            <mat-icon>notifications_off</mat-icon>
            <span>No configurable office notifications are available.</span>
          </div>
        }
      </div>
    }
  </div>
</mat-card>
